/**
 * Web-side client for the Postgres-backed All Team Members directory
 * (`GET /api/v2/members`, plan `plans/all-team-members-directory.md`).
 *
 * Modeled on `libraryApi.ts`: a pure, Jest-testable kernel that reuses the
 * caller's existing sign-in. The Vue layer supplies `idToken` (`getIdToken()`)
 * and `baseUrl`; the page component owns paging, sorting, and filter state and
 * hands a `MembersRequest` in. Postgres decides which rows exist and in what
 * order, already scoped to what the caller may read; every id in the response
 * is one the browser can then bind to Firestore for liveness.
 *
 * Unlike the Library client there is no `X-Device-Id` header: nothing on this
 * endpoint branches on the calling client kind, so the request carries the
 * bearer token and nothing else.
 */

export type MembersSort = 'email' | 'joined' | 'membership_count';
export type MembersOrder = 'asc' | 'desc';
export type MembersStatus = 'pending' | 'joined';

export interface MembersRequest {
	companyId: string;
	// `next_cursor` from the previous page, or absent for the first. Opaque:
	// never build or parse one.
	cursor?: string | null;
	limit?: number;
	sort?: MembersSort;
	order?: MembersOrder;
	q?: string | null;
	// An exact, case-insensitive match on the address: the lookup for one
	// person. `q` is a substring match and can return neighbours.
	email?: string | null;
	status?: MembersStatus | null;
	companyRoleId?: string | null;
	// Filter only: narrows the directory to people on this team. Travels as
	// `in_team_id`, and never decides who the caller is acting as.
	teamId?: string | null;
	// The account to act as: the caller's currently selected team, sent as
	// `team_id` the way every other api2 call names its account.
	accountTeamId?: string | null;
	// Which teams a person is counted through: true for activated, false for
	// deactivated, null or undefined for both, which is the endpoint's own
	// default. The directory asks for the activated reading, so its rows and
	// its total keep matching the company dashboard's card.
	isActive?: boolean | null;
}

export interface MemberTeamRef {
	id: string;
	name: string;
	pending: boolean;
	// Whether the team is activated. The endpoint returns both kinds unless
	// asked otherwise, so a chip can say which this is.
	isActive: boolean;
}

export interface MemberRow {
	// The normalized email: the only identity every membership has.
	key: string;
	email: string;
	userId: string | null;
	isPending: boolean;
	joined: string | null;
	firstInvited: string | null;
	// `membership_teams` on the wire: the teams this person's visible
	// memberships are on, not every team in the company.
	membershipTeams: MemberTeamRef[];
	membershipCount: number;
	hasCompanyRole: boolean;
	inviteOnlyStub: boolean;
	onNoTeam: boolean;
}

export interface MembersPage {
	rows: MemberRow[];
	// Send back as `cursor` for the next page. Null on the last one.
	nextCursor: string | null;
	hasMore: boolean;
}

export interface MembersFetchFailure {
	ok: false;
	reason: 'network' | 'unauthorized' | 'forbidden' | 'failed' | 'stale-cursor';
	// The server's structured error envelope ({ error: { code, message } }),
	// kept so a scope 403 and an identity 403 stay distinguishable.
	code?: string;
	message?: string;
}

export type MembersFetchResult =
	| { ok: true; page: MembersPage }
	| MembersFetchFailure;

/**
 * The failure, or null when the fetch worked.
 *
 * Call this instead of testing `result.ok` yourself. `web` compiles with
 * `strictNullChecks: false`, and TypeScript then narrows a boolean discriminant
 * only by an explicit comparison: inside `if (result.ok)` the success arm is
 * narrowed as you would expect, but after `if (result.ok) return;` (or inside
 * `if (!result.ok)`) the type stays the whole union, so reading `reason` or
 * `code` there does not compile. A `.vue` file is the worst place to discover
 * that, because only the dev server typechecks one. This predicate does the
 * comparison once, in a file the tests typecheck.
 */
export function membersFetchFailure(result: MembersFetchResult): MembersFetchFailure | null {
	return result.ok === true ? null : result;
}

export interface MembersFetchInput {
	baseUrl: string;
	idToken: string;
	request: MembersRequest;
	// Injectable so specs exercise the real request path without patching
	// the global; the page component leaves it unset.
	fetchImpl?: typeof fetch;
}

/**
 * Same origin the Library and identity clients default to, so the directory
 * follows `VUE_APP_API_BASE_URL` in local development exactly as they do.
 */
export const MEMBERS_API_BASE_URL: string =
	process.env.VUE_APP_API_BASE_URL || 'https://api2.rundiffusion.com/api/v2';

export const MEMBERS_MIN_LIMIT = 1;
export const MEMBERS_MAX_LIMIT = 100;
// The endpoint's cap on `q`. A longer value is a 400 for the whole request,
// and `email` is allowed to be longer than this (an address can be).
export const MEMBERS_Q_MAX_LENGTH = 200;
export const MEMBERS_DEFAULT_SORT: MembersSort = 'email';
export const MEMBERS_DEFAULT_ORDER: MembersOrder = 'asc';

function present(value?: string | null): string | null {
	return value && value.trim() ? value.trim() : null;
}

/**
 * Keep the page size inside the endpoint's accepted window so a stale
 * `storeLastPagination` value or a hand-edited URL never turns into a 400.
 */
export function clampMembersLimit(limit: number): number {
	const whole = Math.floor(Number(limit));
	if (!Number.isFinite(whole)) return MEMBERS_MIN_LIMIT;
	return Math.min(MEMBERS_MAX_LIMIT, Math.max(MEMBERS_MIN_LIMIT, whole));
}

/**
 * The translation boundary between the page's camelCase state and the
 * endpoint's snake_case query string, so the URL carries only what deviates
 * from the defaults and never a name the auth layer owns.
 */
export function buildMembersQueryParams(request: MembersRequest): URLSearchParams {
	const params = new URLSearchParams();

	// Which company to read, and which account we are reading it as. They are
	// two different questions and two different parameters.
	//
	// `team_id` is the account selector the auth layer owns: it resolves the
	// caller to a Team Account and asserts membership before the view runs, the
	// way `libraryApi` names the account on every team read. Naming it matters
	// beyond bookkeeping, because a caller who names no team is a Personal
	// Account, and the domain-claim lockdown refuses a Personal Account whose
	// email domain a company has claimed, which is most of the companies this
	// directory is for.
	//
	// `company_id` is what the endpoint actually reads, exactly as `/teams`
	// takes it. The team FILTER is neither of these and travels as `in_team_id`,
	// so filtering to a team never changes who we are acting as.
	params.set('company_id', request.companyId);
	const accountTeamId = present(request.accountTeamId);
	if (accountTeamId) params.set('team_id', accountTeamId);
	// Only when the caller has an opinion: omitting it is the endpoint's
	// "activated and deactivated together".
	if (request.isActive === true || request.isActive === false) {
		params.set('is_active', String(request.isActive));
	}

	const cursor = present(request.cursor);
	if (cursor) params.set('cursor', cursor);
	if (request.limit !== undefined && request.limit !== null) {
		params.set('limit', String(clampMembersLimit(request.limit)));
	}
	if (request.sort && request.sort !== MEMBERS_DEFAULT_SORT) {
		params.set('sort', request.sort);
	}
	if (request.order && request.order !== MEMBERS_DEFAULT_ORDER) {
		params.set('order', request.order);
	}

	const setIf = (key: string, value?: string | null): void => {
		const v = present(value);
		if (v) params.set(key, v);
	};
	// Cut to the endpoint's cap: a search the server would refuse outright
	// becomes the closest search it will run.
	const q = present(request.q);
	setIf('q', q ? q.slice(0, MEMBERS_Q_MAX_LENGTH) : null);
	setIf('email', request.email);
	setIf('status', request.status);
	setIf('company_role_id', request.companyRoleId);
	setIf('in_team_id', request.teamId);

	return params;
}

/**
 * The request carries the bearer token and nothing else, so the header set is
 * pinned here rather than growing a device id by analogy with the Library.
 */
export function authedHeaders(idToken: string): Record<string, string> {
	return {
		'Authorization': `Bearer ${idToken}`,
	};
}

function objectRecord(value: unknown): Record<string, unknown> | null {
	return value !== null && typeof value === 'object' && !Array.isArray(value)
		? value as Record<string, unknown>
		: null;
}

function stringOrNull(value: unknown): string | null {
	return typeof value === 'string' && value ? value : null;
}

function numberOr(value: unknown, fallback: number): number {
	const n = Number(value);
	return Number.isFinite(n) ? n : fallback;
}

function parseTeamRef(candidate: unknown): MemberTeamRef | null {
	const team = objectRecord(candidate);
	const id = team ? stringOrNull(team.id) : null;
	if (!team || !id) return null;
	return {
		id,
		name: typeof team.name === 'string' ? team.name : '',
		pending: !!team.pending,
		// Absent on a server that predates the field: those only ever returned
		// activated teams, so that reading is the safe one.
		isActive: team.is_active === undefined ? true : !!team.is_active,
	};
}

function parseMemberRow(candidate: unknown): MemberRow | null {
	const row = objectRecord(candidate);
	if (!row) return null;
	const email = stringOrNull(row.email) || '';
	const membershipTeams = Array.isArray(row.membership_teams)
		? row.membership_teams.map(parseTeamRef).filter((t): t is MemberTeamRef => t !== null)
		: [];
	return {
		key: stringOrNull(row.key) || email,
		email,
		userId: stringOrNull(row.user_id),
		isPending: !!row.is_pending,
		joined: stringOrNull(row.joined),
		firstInvited: stringOrNull(row.first_invited),
		membershipTeams,
		membershipCount: numberOr(row.membership_count, membershipTeams.length),
		hasCompanyRole: !!row.has_company_role,
		inviteOnlyStub: !!row.invite_only_stub,
		onNoTeam: !!row.on_no_team,
	};
}

/**
 * Normalize the wire envelope into the page's camelCase model so the row
 * component and its state reducer never see snake_case or a missing field.
 */
export function parseMembersPage(body: unknown): MembersPage {
	const envelope = objectRecord(body) || {};
	const rows = Array.isArray(envelope.data)
		? envelope.data.map(parseMemberRow).filter((r): r is MemberRow => r !== null)
		: [];
	return {
		rows,
		nextCursor: stringOrNull(envelope.next_cursor),
		hasMore: !!envelope.has_more,
	};
}

/**
 * Best-effort read of the API's error envelope: `{ error: { code, message } }`.
 * Never throws, so a non-JSON or empty error body just yields empty fields.
 */
async function readErrorEnvelope(resp: Response): Promise<{ code?: string; message?: string }> {
	try {
		const body: unknown = await resp.json();
		const err: Record<string, unknown> = objectRecord(objectRecord(body)?.error) || {};
		return {
			code: typeof err.code === 'string' ? err.code : undefined,
			message: typeof err.message === 'string' ? err.message : undefined,
		};
	} catch {
		return {};
	}
}

/**
 * One page of the directory as a result union, so the page component can
 * branch on why a read failed without ever catching.
 */
export async function fetchMembersPage(input: MembersFetchInput): Promise<MembersFetchResult> {
	// No company, no request: the endpoint would answer 400, and the page has
	// nothing to show for it. Refuse here, the way the Library refuses an
	// unnamed account, so the caller can tell "not ready" from "server said no".
	if (!present(input.request.companyId)) {
		return { ok: false, reason: 'failed', code: 'COMPANY_REQUIRED', message: 'A company is required' };
	}
	const qs = buildMembersQueryParams(input.request).toString();
	const url = `${input.baseUrl.replace(/\/$/, '')}/members?${qs}`;
	const fetchImpl: typeof fetch = input.fetchImpl || window.fetch;

	let resp: Response;
	try {
		resp = await fetchImpl(url, {
			method: 'GET',
			headers: authedHeaders(input.idToken),
		});
	} catch {
		return { ok: false, reason: 'network' };
	}

	if (!resp.ok) {
		// An empty viewer scope is a 403 with its own code, as is an unverified
		// email; keeping the envelope is what tells them apart.
		const { code, message } = await readErrorEnvelope(resp);
		// INVALID_CURSOR is its own outcome, not a failure to report: it means
		// the position we resumed from no longer belongs to this listing, and
		// the page answers by starting the walk again rather than by apologising.
		const reason: MembersFetchFailure['reason'] = code === 'INVALID_CURSOR'
			? 'stale-cursor'
			: (resp.status === 401
				? 'unauthorized'
				: (resp.status === 403 ? 'forbidden' : 'failed'));
		return {
			ok: false,
			reason,
			...(code ? { code } : {}),
			message: message || `HTTP ${resp.status}`,
		};
	}

	let body: unknown;
	try {
		body = await resp.json();
	} catch {
		return { ok: false, reason: 'failed', message: 'Invalid JSON' };
	}

	return { ok: true, page: parseMembersPage(body) };
}
